import moment from 'moment';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import keyBy from 'lodash/keyBy';
import pickBy from 'lodash/pickBy';
import filter from 'lodash/filter';

import * as types from '../actions/actionTypes';
import { IinitialState } from '.';
import { ImeasurementPointAnswer, ImeasurementPointResult } from '../models';
import { getSelectedJobID } from './commonSelectors';
import { initialMeasurementPointResultAnswer, initialMeasurmentPointResult } from './initialState';

const cleanResult = (result: ImeasurementPointResult): ImeasurementPointResult => {
    let cleanedAnswers: ImeasurementPointAnswer[] = [];
    if (result.measurementPointAnswers && result.measurementPointAnswers.length) {
        cleanedAnswers = result.measurementPointAnswers.map(answer => {
            return {
                ...initialMeasurementPointResultAnswer,
                ...pickBy(answer, (property, key) => property !== null)
            };
        });
    }
    return {
        ...initialMeasurmentPointResult,
        ...pickBy(result, (property, key) => property !== null),
        measurementPointAnswers: cleanedAnswers
    };
};
/*
 * SELECTORS
 */

export const selectMostRecentTemporaryResult = (
    state: { [key: string]: ImeasurementPointResult },
    installBaseID: string,
    jobID: string,
    temporary: boolean
) => {
    const filteredResults = filter(state, { installBaseID, jobID, temporary });
    if (filteredResults.length === 0) {
        return initialMeasurmentPointResult;
    }
    return filteredResults.reduce((previous, current) => {
        if (moment.utc(previous.updateDate).isAfter(moment.utc(current.updateDate))) {
            return previous;
        } else {
            return current;
        }
    }, initialMeasurmentPointResult);
};

/*
 * selectMostRecentResult
 * find the most recent result for this install and MPL
 */
export const selectMostRecentResult = (
    state: { [key: string]: ImeasurementPointResult },
    installBaseID: string,
    MPListID: string
) => {
    const filteredResults = filter(state, {
        installBaseID,
        measurementPointListID: MPListID
    });
    if (filteredResults.length === 0) {
        return initialMeasurmentPointResult;
    }
    return filteredResults.reduce((previous, current) => {
        if (moment.utc(previous.updateDate).isAfter(moment.utc(current.updateDate))) {
            return previous;
        } else {
            return current;
        }
    }, initialMeasurmentPointResult);
};

/*
 * True when candidate is the later of the two saves. Ordered on createDate with id breaking ties,
 * mirroring MeasurementPointHelper.LatestResultIDPerJob on the API so both layers name the same
 * result for a job.
 */
const compareRecency = (a: ImeasurementPointResult, b: ImeasurementPointResult): number => {
    const aDate = moment.utc(a.createDate);
    const bDate = moment.utc(b.createDate);

    if (!aDate.isSame(bDate)) {
        return aDate.isAfter(bDate) ? -1 : 1;
    }
    if (a.id === b.id) {
        return 0;
    }
    return a.id > b.id ? -1 : 1;
};

const isLaterResult = (candidate: ImeasurementPointResult, current: ImeasurementPointResult): boolean =>
    compareRecency(candidate, current) < 0;

/*
 * How many jobs of history the asset shows for each job type. Mirrors
 * MeasurementPointHelper.HistoryJobLimit on the API - five inspections show the three most recent,
 * and the asset's verifications are capped separately from them.
 */
export const HISTORY_JOB_LIMIT = 3;

/*
 * The job type a result belongs to.
 *
 * Only the history endpoint stamps jobTypeID onto the result itself, so results that arrived with
 * the paged job load carry none, and a result saved locally can carry an empty GUID - see the same
 * two cases in MeasurementPointListForm's answer pre-fill. Either way the type is read from the job
 * store. Results whose job is in neither place share one bucket, which is capped like any other:
 * the alternative is letting an unbounded set of untyped rows back into the list the cap exists to
 * shorten.
 */
const EMPTY_JOB_TYPE_ID = '00000000-0000-0000-0000-000000000000';
const UNKNOWN_JOB_TYPE = 'unknown';

const jobTypeIDForResult = (state: IinitialState, result: ImeasurementPointResult): string => {
    const stamped = result.jobTypeID !== undefined && result.jobTypeID !== EMPTY_JOB_TYPE_ID ? result.jobTypeID : '';

    return stamped || state.manageJob.jobsByID[result.jobID]?.jobTypeID || UNKNOWN_JOB_TYPE;
};

/*
 * The install's saved results, one per job, for the most recent HISTORY_JOB_LIMIT jobs of each job
 * type.
 *
 * Save & Log inserts a new result every time it is pressed - re-opening an asset seeds a fresh GUID
 * from the previous answers - so a job accumulates a row per save. The API collapses these for the
 * history endpoint, but that is not enough on its own: measurementPointResultsByID also fills from
 * the paged job load and from every local save (UPDATE_MEASUREMENT_POINT_RESULT_BULK), and the
 * reducers only ever merge into it, so the duplicates are already in the store before the history
 * endpoint is ever called. They have to be collapsed on the way out too.
 *
 * The same goes for the job cap. GetMeasurementPointListResultHistory only ever sends three jobs of
 * the type it was asked for, but the store keeps everything it has ever seen and is persisted
 * offline, so an asset with five inspections still listed five of them. Capping on read also means
 * devices whose store already holds the older jobs fix themselves, with no cache clear.
 *
 * Ranked on createDate with id breaking ties - the rule the collapse and the API both use.
 * updateDate is set by the client and cannot be trusted once results are queued offline and
 * replayed, so it is not used to decide which jobs survive, even though the history list is sorted
 * by it (selectInstallBaseHistoryByInstallBaseID).
 */
export const selectMeasurementPointListResultsByInstallBaseID = (state: IinitialState, installBaseID: string) => {
    const results = filter(
        state.measurementPointResults.measurementPointResultsByID,
        result => result.installBaseID === installBaseID && result.isDeleted === false && result.temporary !== true
    );

    const latestByJobID = new Map<string, ImeasurementPointResult>();
    results.forEach(result => {
        const current = latestByJobID.get(result.jobID);
        if (current === undefined || isLaterResult(result, current)) {
            latestByJobID.set(result.jobID, result);
        }
    });

    const jobsByType = new Map<string, ImeasurementPointResult[]>();
    latestByJobID.forEach(result => {
        const jobTypeID = jobTypeIDForResult(state, result);
        const jobsOfType = jobsByType.get(jobTypeID);
        if (jobsOfType === undefined) {
            jobsByType.set(jobTypeID, [result]);
        } else {
            jobsOfType.push(result);
        }
    });

    const recent: ImeasurementPointResult[] = [];
    jobsByType.forEach(jobsOfType => {
        recent.push(...jobsOfType.sort(compareRecency).slice(0, HISTORY_JOB_LIMIT));
    });

    return recent;
};

const selectMPListIDProp = (state: IinitialState, props: { MPListID: string }) => props.MPListID;
const selectMPLRbyID = (state: IinitialState) => state.measurementPointResults.measurementPointResultsByID;
const getHistoricalResultID = (state: IinitialState) => state.measurementPointResults.historicalResultID;

export const selectHistoricalResultID = createSelector(
    [getHistoricalResultID],
    historicalResultID => historicalResultID
);

export const selectMPLRsForJobAndMPL = createSelector(
    [selectMPLRbyID, getSelectedJobID, selectMPListIDProp],
    (measurementPointResultsByID, jobID, MPListID) => {
        return filter(measurementPointResultsByID, {
            jobID,
            measurementPointListID: MPListID,
            isDeleted: false
        });
    }
);

/*
 * REDUCERS
 */

const measurementPointResultsByIDReducer = (
    state: { [key: string]: ImeasurementPointResult } = {},
    action: any
): { [key: string]: ImeasurementPointResult } => {
    switch (action.type) {
        case types.ADD_MEASUREMENT_POINT_RESULT:
            return { ...state, [action.result.id]: cleanResult(action.result) };
        case types.UPDATE_MEASUREMENT_POINT_RESULT:
            return { ...state, [action.result.id]: action.result };
        case types.UPDATE_MEASUREMENT_POINT_RESULT_STATUS:
            return { ...state, [action.result.id]: cleanResult(action.result) };
        case types.UPDATE_MEASUREMENT_POINT_RESULT_BULK: {
            return { ...state, ...keyBy(action.results, 'id') };
        }
        case types.SET_MEASUREMENT_POINT_RESULT_HISTORY: {
            const newResults = keyBy(
                action.payload.data.map((res: ImeasurementPointResult) => cleanResult(res)),
                'id'
            );
            return { ...state, ...newResults };
        }
        case types.GET_MEASUREMENT_POINT_FACILITY_RESULTS_SUCCESS: {
            let results = action.results;
            if (action.payload && action.payload.data && action.payload.data.length > 0) {
                results = action.payload.data;
            }

            const newResults = keyBy(
                results.map((res: ImeasurementPointResult) => cleanResult(res)),
                'id'
            );

            return { ...state, ...newResults };
        }
        case types.USER_LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
};

const selectedResult = (
    state: ImeasurementPointResult = initialMeasurmentPointResult,
    action: any
): ImeasurementPointResult => {
    switch (action.type) {
        case types.ADD_MEASUREMENT_POINT_RESULT:
            return action.result;
        case types.UPDATE_MEASUREMENT_POINT_RESULT:
            if (state.id === action.result.id) {
                return { ...state, ...cleanResult(action.result) };
            }
            return cleanResult(action.result);
        case types.USER_LOGOUT_SUCCESS:
            return initialMeasurmentPointResult;
        default:
            return state;
    }
};

/*
 * enable viewing of historical results
 */
const historicalResultID = (state = '', action: any): string => {
    switch (action.type) {
        case types.SET_HISTORICAL_RESULT_ID:
            return action.resultID;
        // The mirror of historicalWorkOrderID's rule: opening a work order closes any result that
        // was open, so the two never both point at a row (MMG-4345).
        case types.SET_SELECTED_HISTORICAL_WORKORDER_ID:
        case types.CLEAR_HISTORICAL_RESULT_ID:
            return '';
        case types.USER_LOGOUT_SUCCESS:
            return '';
        default:
            return state;
    }
};

const historicalMPToCopy = (state = initialMeasurmentPointResult, action: any): any => {
    switch (action.type) {
        case types.SET_HISTORICAL_MP_RESULT:
            return action.payload;
        case types.CLEAR_HISTORICAL_MP_RESULT:
            return initialMeasurmentPointResult;
        case types.USER_LOGOUT_SUCCESS:
            return initialMeasurmentPointResult;
        default:
            return state;
    }
};

const measurementPointResultsReducer = combineReducers({
    measurementPointResultsByID: measurementPointResultsByIDReducer,
    historicalResultID,
    selectedResult,
    historicalMPToCopy
});

export default measurementPointResultsReducer;
